import React from "react";
import { makeObservable, observable, runInAction } from "mobx";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { observer } from "mobx-react";

import type { UserData } from "./models";

/** Logged-out share: stash the token so Chat can stage it after login. */
function ShareInboxRedirect({ token }: { token: string }) {
  const navigate = useNavigate();
  sessionStorage.setItem('shareToken', token);
  navigate('/', { replace: true });
  return null;
}

import apiService from "@services/apiService";
import { userService } from "@services/userService";

import ErrorBoundary from "@components/ErrorBoundary";
import { ToastContainer } from "@components/Toast";
import Page404 from "@pages/Page404";
import Chat from "@pages/Chat/Chat";
import ClientError from "@pages/ClientError";
import LoginPage from "@pages/LoginPage";
import SharedChat from "@pages/SharedChat";
import type { SuggestionsAdminPageProps } from "@pages/SuggestionsAdminPage";
const SuggestionsAdminPageLazy = React.lazy<React.ComponentType<SuggestionsAdminPageProps>>(() => import("@pages/SuggestionsAdminPage"));

/** Wraps the lazy admin page in a Suspense boundary, forwarding props so Chat's cloneElement still injects sidebar props. */
function SuggestionsAdminPage(props: SuggestionsAdminPageProps) {
  return <React.Suspense fallback={null}><SuggestionsAdminPageLazy {...props} /></React.Suspense>;
}

import './App.css';
import './Scrollbars.css';
import './components/ContextMenu/_context-menu.scss';

declare global {
  interface Window {
    user?: UserData;
  }
}

@observer
class App extends React.Component {
  @observable UserData = window.user;
  @observable isLoading = true;

  constructor(props: {}) {
    super(props);
    makeObservable(this);
  }
  
  componentDidMount(): void {
    void this._checkUserLoggedIn();
  }

  render(){

    if (this.isLoading) {
      return null;
    }

    const isLoggedIn = !!window.user;
    return (
      <BrowserRouter>
        <ToastContainer />
        <ErrorBoundary fallback={(e) => <ClientError error={e} />}>
          <Routes>
            {!isLoggedIn ? (
              <>
                <Route
                  path="/share/:shareId"
                  element={
                    <SharedChat
                      shareId={
                        window.location.pathname.split("/share/")[1] || ""
                      }
                    />
                  }
                />              
                <Route path="/share-inbox/:token" element={<ShareInboxRedirect token={window.location.pathname.split("/share-inbox/")[1] || ""} />} />
                <Route path="/" element={<LoginPage />} />
                <Route path="*" element={<Page404 />} />
              </>
            ) : (
              <>
                <Route path="/share-inbox/:token" element={<Chat />} />
                <Route path="/" element={<Chat />} />
                <Route path="/suggestions" element={<Chat><SuggestionsAdminPage /></Chat>} />
                <Route path="/c/:chatId" element={<Chat />} />
                <Route path="/c/:chatId/files" element={<Chat />} />
                <Route path="/c/:chatId/files/*" element={<Chat />} />
                <Route
                  path="/share/:shareId"
                  element={
                    <SharedChat
                      shareId={
                        window.location.pathname.split("/share/")[1] || ""
                      }
                    />
                  }
                />
                <Route path="*" element={<Page404 />} />
              </>
            )}
          </Routes>
        </ErrorBoundary>
      </BrowserRouter>
    );
  }
  
  private async _checkUserLoggedIn() {
    let isLoggedIn = !!window.user;
    if (!isLoggedIn) {
      const userDataResponse = await fetch('/auth/userinfo');
      if (userDataResponse.ok) {
        const userData: { isAuthenticated: boolean; user?: UserData; hasRefreshToken?: boolean } = await userDataResponse.json();
        if (userData.isAuthenticated && userData.user) {
          window.user = userData.user;
          this.UserData = userData.user;
        }

        if (userData.hasRefreshToken) {
          await apiService.refreshToken(void 0, false);
        }
      }
    }
    runInAction(() => {
      this.isLoading = false;
    });
    await userService.loadUserSettings();
  }

};

export default App;